import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetMerchantQuery } from 'services/merchants';

import { LoadingScreen } from 'pages/loading/loading';

import RoutePath from 'router/path';

import { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectAccountType, selectAccountUuid } from 'state/slices/accountSlice';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel, AccountType } from 'types/account';
import { MerchantFlags, RouteSetup, RouteSetupFactory } from 'types/routing';
import { UserAccessLevel } from 'types/user';

import { AuthenticationGuard } from './authenticationGuard';
import Routes from './routes';
import AdminRouterSetup from './routes/admin';
import PartnerRouterSetup, { getDefaultLoginRedirectRoute } from './routes/partner';
import { getPartnerFeatures } from './routes/partner/features';
import SuperAdminRouterSetup from './routes/superAdmin';
import SupportRouterSetup from './routes/support';
import SuspendedRouterSetup from './routes/suspended';

interface GetRoutesArgs {
  userLevel: UserAccessLevel;
  accountAccessLevel?: AccountAccessLevel;
  isSuspended?: boolean;
  hasAccounts?: boolean;
  accountType?: AccountType;
  merchantFlags?: MerchantFlags;
}

const routerSetupMap: Record<UserAccessLevel, RouteSetup | RouteSetupFactory> = {
  [UserAccessLevel.Admin]: AdminRouterSetup,
  [UserAccessLevel.SuperAdmin]: SuperAdminRouterSetup,
  [UserAccessLevel.Partner]: PartnerRouterSetup,
  [UserAccessLevel.Support]: SupportRouterSetup,
};

const getRoutes = ({ userLevel, isSuspended, accountAccessLevel, hasAccounts, accountType, merchantFlags }: GetRoutesArgs): RouteSetup => {
  if (isSuspended) {
    return SuspendedRouterSetup;
  }

  const selectedSetup = routerSetupMap[userLevel];
  if (typeof selectedSetup === 'function') {
    return selectedSetup({
      accountAccessLevel,
      hasAccounts,
      accountType,
      merchantFlags,
    });
  }

  return selectedSetup;
};

export const RoutesContainer = () => {
  const navigate = useNavigate();
  const userLevel = useSelector(selectUserLevel);
  const accountType = useSelector(selectAccountType);
  const accountAccessLevel = useSelector(selectAccountAccess);
  const accountUuid = useSelector(selectAccountUuid);
  const isSuspended = false;

  const isPartner = userLevel === UserAccessLevel.Partner;
  const isPwcPartner = isPartner && accountType === AccountType.Pwc;

  const hasAccounts = isPartner ?? undefined;

  /** Last account uuid after we applied post-switch navigation (or initial sync). */
  const previousPartnerAccountUuidRef = useRef<string | null>(null);

  const {
    data: merchantData,
    isLoading: isMerchantLoading,
    isFetching: isMerchantFetching,
    currentData: currentMerchantData,
  } = useGetMerchantQuery(accountUuid || '', {
    skip: !isPwcPartner || !accountUuid,
  });

  const merchantFlags =
    isPwcPartner && merchantData?.data
      ? {
          pwcEnabled: merchantData.data.pwcEnabled,
          cryptoPayoutEnabled: merchantData.data.cryptoPayoutEnabled,
        }
      : undefined;

  useEffect(() => {
    if (!isPartner) {
      previousPartnerAccountUuidRef.current = accountUuid || null;
      return;
    }

    if (!accountUuid) {
      previousPartnerAccountUuidRef.current = null;
      return;
    }

    const previousUuid = previousPartnerAccountUuidRef.current;
    const accountSwitched = previousUuid !== null && previousUuid !== accountUuid;

    if (!accountSwitched) {
      previousPartnerAccountUuidRef.current = accountUuid;
      return;
    }

    if (accountType === AccountType.Ramp) {
      navigate(RoutePath.Ramp.Orders.Root, { replace: true });
      previousPartnerAccountUuidRef.current = accountUuid;
      return;
    }

    if (accountType === AccountType.Pwc) {
      if (!merchantFlags || isMerchantFetching) {
        return;
      }
      navigate(getDefaultLoginRedirectRoute(true, accountType, getPartnerFeatures(accountType, merchantFlags)), { replace: true });
      previousPartnerAccountUuidRef.current = accountUuid;
      return;
    }

    navigate(getDefaultLoginRedirectRoute(true, accountType, getPartnerFeatures(accountType, undefined)), {
      replace: true,
    });
    previousPartnerAccountUuidRef.current = accountUuid;
  }, [isPartner, accountUuid, accountType, merchantFlags, isMerchantFetching, navigate]);

  if (!userLevel) {
    return <AuthenticationGuard component={LoadingScreen} />;
  }

  const isMerchantDataStale = isPwcPartner && merchantData?.data && merchantData.data.uuid !== accountUuid;
  if (isPwcPartner && (isMerchantLoading || isMerchantFetching || isMerchantDataStale || !currentMerchantData)) {
    return <AuthenticationGuard component={LoadingScreen} />;
  }

  const routeSetup = getRoutes({ userLevel, isSuspended, accountAccessLevel, hasAccounts, accountType, merchantFlags });

  return <Routes routeSetup={routeSetup} />;
};

export default RoutesContainer;
